// * External
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Button, ProgressIndicator } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
import { StarsTwoFilled } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import client from "lib/apolloClient/client";
import { compose } from "lib/apolloClient";

// * Internal
import { updateExtractFromDoc } from "Worksheet/modules/WorksheetModules";
import { aiExtractQuestionsMutation } from "Worksheet/modules/WorksheetMutations";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { useI18n } from "Hooks";

// * Relative
import LearningManager from "./LearningManager";
import FileAndFormPicker from "./FileAndFormPicker";
import classes from "./ExtractFromDoc.scss";
import { getTargetedContentField } from "../AiQuestionGenerator/AiQuestionGeneratorUtils";
import { getCourseAssignmentFeedWithoutAssignedStudentQuery } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/ClassRoomQuery";
import { getQueryFiltersMemoize } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/ClassRoomModule";
import { getExtractionLoaderText } from "Worksheet/modules/WorksheetUtils";
import Loading from "UIComponents/Loading";
import { createAIConversation } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningAssistantModule";

/**
 *
 * @param {*} props
 * @returns Extract from Doc component
 */

const styles = {
  progressBar: {
    width: "100%",
    backgroundColor: "var(--surface-secondary-hover)",
  },
  disabled: { pointerEvents: "none", opacity: "0.7" },
};

const ExtractFromDoc = props => {
  const {
    addSection,
    toggleAiQuestionGenerator,
    sourceFrom,
    classSubjects,
    mode,
    assessmentId,
    updateExtractFromDoc,
    isExtracting,
    curriculumType,
    curriculumProgramId,
    runningProgress,
    setToastMsg,
    conversationMetadata,
    conversationAttachments,
    extractionConversationId,
    courseId,
    isToddleLibraryPortal,
    draftInput,
    getExtractionAIConversationsRefetch,
    assignmentFeedFilters,
    academicYearId,
    getAssessmentRefetch,
    progress,
  } = props;

  const { t } = useI18n(["common", "worksheet", "toastMsgs"]);
  const [attachments, setAttachments] = useState([]);
  const [context, setContext] = useState({});
  const [learningGoalsFieldsData, setLearningGoalsFieldsData] = useState({});
  const [_learningGoalFieldsData, _setLearningGoalFieldsData] = useState([]);
  const [uploadingFile, setIsUploadingFile] = useState(null);
  const [extractionStarted, setExtractionStarted] = useState(false);

  useEffect(() => {
    if (isExtracting) {
      setExtractionStarted(false);
    }
  }, [isExtracting]);

  // * Targeted content based on curriculum type
  const TARGETED_CONTENT = useMemo(() => {
    return getTargetedContentField(curriculumType);
  }, [curriculumType]);

  const handleExtraction = async () => {
    setExtractionStarted(true);
    updateExtractFromDoc({
      assessmentId,
      data: { isExtracting: true, runningProgress: 0, progress: 0 },
    });

    const learningGoals = learningGoalsFieldsData || {};

    if (!_.isEmpty(_.get(context, TARGETED_CONTENT))) {
      learningGoals[TARGETED_CONTENT] = _.get(context, TARGETED_CONTENT);
    }

    const attachmentsPayload =
      _.map(attachments, attachment => {
        return {
          name: attachment.name,
          type: attachment.type,
          mimeType: attachment.mimeType,
          id: attachment.id,
          metadata: {
            size: _.get(attachment, "metadata.size", 0),
          },
        };
      }) || [];

    const payload = {
      input: {
        courseId,
        draftInput,
        assessmentId,
        learningGoals,
        aiConversationId: extractionConversationId,
        curriculumProgramId,
        attachmentIds: _.map(attachments, "id"),
        metadata: {
          learningGoals: _learningGoalFieldsData,
          attachments: attachmentsPayload,
        },
      },
    };

    try {
      // refetching the assignment feed to get hold of the latest data required to show extraction loader in the feed list
      const refetchQueries = !_.isEmpty(courseId)
        ? [
            {
              query: getCourseAssignmentFeedWithoutAssignedStudentQuery,
              variables: {
                courseId,
                ...getQueryFiltersMemoize({
                  filters: assignmentFeedFilters,
                  academicYearId,
                }),
              },
              context: {
                useHttpLink: true,
                ...(isToddleLibraryPortal ? { portalType: "PLANNER" } : {}),
              },
            },
          ]
        : [];
      await client.mutate({
        mutation: aiExtractQuestionsMutation,
        variables: payload,
        // Library route defaults auth to COMMUNITY (oid: "-7"); force PLANNER to use teacher's real org token.
        context: isToddleLibraryPortal ? { portalType: "PLANNER" } : undefined,
        refetchQueries,
      });
      const response = await getAssessmentRefetch();

      const isWorksheetExtractionInProgress = _.get(
        response,
        "data.node.isWorksheetExtractionInProgress",
        false
      );
      if (isWorksheetExtractionInProgress) {
        setTimeout(() => setExtractionStarted(false), 1500);
      }
      // refetching the AI conversations to get extract from doc metadata
      await getExtractionAIConversationsRefetch();
    } catch (error) {
      updateExtractFromDoc({
        assessmentId,
        data: { isExtracting: false, runningProgress: 0, progress: 0 },
      });
      setToastMsg({
        msg: "toastMsgs:questions_not_extracted",
        type: "error",
      });
    }
  };

  const onUpdateLearningGoals = ({
    learningGoals,
    newLearningGoalsFieldData,
  }) => {
    _setLearningGoalFieldsData(newLearningGoalsFieldData);
    setLearningGoalsFieldsData(learningGoals);
  };

  const progressPercentageClasses = classNames(
    "text-body-s",
    classes.textSecondary
  );

  const isExtractionStarted = isExtracting || extractionStarted;

  return (
    <div className={classes.container}>
      <div
        className={classes.contentContainer}
        style={isExtracting ? styles.disabled : {}}
      >
        <FileAndFormPicker
          attachments={attachments}
          setAttachments={setAttachments}
          uploadingFile={uploadingFile}
          setIsUploadingFile={setIsUploadingFile}
          conversationAttachments={conversationAttachments}
          extractionConversationId={extractionConversationId}
        />
        <LearningManager
          assessmentId={assessmentId}
          addSection={addSection}
          toggleAiQuestionGenerator={toggleAiQuestionGenerator}
          sourceFrom={sourceFrom}
          classSubjects={classSubjects}
          mode={mode}
          context={context}
          setContext={setContext}
          isExtractFromDoc={true}
          onChangeTargetedContent={(key, value) => setContext({ [key]: value })}
          onChangeLearningGoals={onUpdateLearningGoals}
          conversationMetadata={conversationMetadata}
        />
        {!isExtractionStarted ? (
          <div>
            <Button
              dsVersion="2.0"
              variant={"progressive"}
              type={_.isEmpty(attachments) ? "outlined" : "fill"}
              size="medium"
              onClick={handleExtraction}
              disabled={_.isEmpty(attachments) || uploadingFile}
              testId="worksheet-extract-from-doc-button"
              icon={<StarsTwoFilled />}
            >
              {t("common:extract_with_label", {
                label: t("worksheet:questions"),
              })}
            </Button>
          </div>
        ) : null}
      </div>
      {isExtractionStarted && (
        <div className={classes.progressContainer}>
          <div className={classes.progressTextContainer}>
            <span className="text-body-s">
              {t(getExtractionLoaderText(progress))}
            </span>
            <span
              className={progressPercentageClasses}
            >{`${runningProgress}%`}</span>
          </div>
          <ProgressIndicator
            variant="progress-bar"
            progress={runningProgress}
            status="success"
            containerStyle={styles.progressBar}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumType = getCurrentCurriculumProgramType(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const portalType = _.get(state, "login.userInfo.portalType");

  const isToddleLibraryPortal =
    _.get(state, "login.activeTab") === "TODDLE_LIBRARY";
  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  const assessmentId = _.get(ownProps, "assessmentId", null);

  const isExtracting =
    _.get(
      state,
      `worksheet.extractFromDoc.${assessmentId}.isExtracting`,
      false
    ) && _.get(ownProps, "isWorksheetExtractionInProgress", false);

  const runningProgress = _.get(
    state,
    `worksheet.extractFromDoc.${assessmentId}.runningProgress`,
    0
  );

  const progress = _.get(
    state,
    `worksheet.extractFromDoc.${assessmentId}.progress`,
    0
  );

  const draftInput = null;

  const assignmentFeedFilters = _.get(state, "classRoom.filters", {});

  return {
    courseId,
    isToddleLibraryPortal,
    portalType,
    curriculumType,
    curriculumProgramId,
    isExtracting,
    runningProgress,
    draftInput,
    assignmentFeedFilters,
    academicYearId,
    progress,
  };
};

const mapDispatchToProps = {
  updateExtractFromDoc,
  setToastMsg,
  createAIConversation,
};

const ExtractFromDocWrapper = props => {
  const {
    assessmentId,
    extractionConversationId,
    createAIConversation,
    getAssessmentRefetch,
  } = props;

  useEffect(() => {
    if (!extractionConversationId) {
      const createNewConversation = async () => {
        await createAIConversation({
          input: {
            entityId: assessmentId,
            entityType: "ASSESSMENT",
            entitySubType: "WORKSHEET_EXTRACTION",
          },
        });

        await getAssessmentRefetch();
      };

      createNewConversation();
    }
  }, [extractionConversationId]);

  if (!extractionConversationId) return <Loading />;

  return <ExtractFromDoc {...props} />;
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ExtractFromDocWrapper
);
